import { useRef, useState, useCallback } from 'react';

/**
 * Bottom-sheet swipe-to-close gesture pro mobile modaly.
 *
 * Vrací:
 *   - `dragHandleProps`: rozprostřít na **horní hranu modalu** (drag indicator
 *     + jeho touch zóna). Touch start MUSÍ začít zde — tím nezasahujeme do
 *     scrollování obsahu modalu níže.
 *   - `modalStyle`: aplikovat na vnější kontejner modalu — během tahu
 *     translateY, po puštění buď close (přes threshold) nebo snap-back.
 *
 * Použití:
 * ```jsx
 * const { dragHandleProps, modalStyle } = useBottomSheetSwipe(onClose, { enabled: isMobile });
 * <div style={{ ...containerStyle, ...modalStyle }}>
 *     <div {...dragHandleProps}>{drag indicator visual}</div>
 *     {modal content}
 * </div>
 * ```
 */
export default function useBottomSheetSwipe(onClose, { threshold = 80, enabled = true } = {}) {
    const startY = useRef(null);
    const [dragY, setDragY] = useState(0);
    const [dragging, setDragging] = useState(false);

    const onTouchStart = useCallback((e) => {
        if (!enabled) return;
        startY.current = e.touches[0].clientY;
        setDragging(true);
    }, [enabled]);

    const onTouchMove = useCallback((e) => {
        if (!enabled || startY.current == null) return;
        const dy = e.touches[0].clientY - startY.current;
        // Jen tah dolů — tah nahoru ignorujeme.
        setDragY(Math.max(0, dy));
    }, [enabled]);

    const finalize = useCallback(() => {
        if (!enabled) return;
        setDragging(false);
        if (dragY > threshold) {
            onClose?.();
        }
        // Reset je vždy — pokud onClose proběhl, modal stejně zmizí; pokud ne,
        // snap-back se animuje díky `transition: transform 0.25s` v modalStyle.
        setDragY(0);
        startY.current = null;
    }, [enabled, dragY, threshold, onClose]);

    const dragHandleProps = {
        onTouchStart,
        onTouchMove,
        onTouchEnd: finalize,
        onTouchCancel: finalize,
    };

    const modalStyle = {
        transform: enabled && dragY > 0 ? `translateY(${dragY}px)` : undefined,
        transition: dragging ? 'none' : 'transform 0.25s ease',
        touchAction: enabled ? 'pan-y' : undefined,
    };

    return { dragHandleProps, modalStyle, dragging };
}
